import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Barcode as BarcodeIcon,
  Check,
  EyeOff,
  RefreshCw,
  Search,
  Send,
  XCircle,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Barcode,
  Button,
  ConfirmDialog,
  EmptyState,
  Gtin,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
} from '../ui/index.js';
import { ApiError, fetchJson } from '../lib/fetch-json.js';
import { money, stamp, toCapitalizedCase } from '../lib/format.js';

interface CorrectionRow {
  readonly id: number;
  readonly wcProductId: number;
  readonly wcParentId: number | null;
  readonly isVariation: boolean;
  readonly sku: string | null;
  readonly name: string;
  readonly brand: string | null;
  readonly image: string | null;
  readonly oldBarcode: string | null;
  readonly coreGtin?: string | null;
  readonly pluginEan?: string | null;
  readonly newBarcode: string | null;
  readonly isSingleField?: boolean;
  readonly canAdoptSiteBarcode?: boolean;
  readonly siteBarcode?: string | null;
  readonly duplicateConflict?: {
    readonly productId: number;
    readonly sku: string | null;
    readonly name: string;
    readonly brand: string | null;
    readonly field: string;
    readonly matchedBarcode: string;
  } | null;
  readonly lastFailedError?: string | null;
  readonly lastFailedBatchId?: number | null;
  readonly lastFailedAt?: string | null;
  readonly lastSeenAt?: string | null;
  readonly wcModifiedAt?: string | null;
  readonly purchasePriceHt: string | number | null;
  readonly purchaseSourceKey: string | null;
}

interface WcBatch {
  readonly id: number;
  readonly env: string;
  readonly status: string;
  readonly createdBy: string;
  readonly approvedBy: string | null;
  readonly total: number;
  readonly succeeded: number;
  readonly failed: number;
  readonly createdAt: string;
  readonly finishedAt: string | null;
  readonly changeCount: number;
}

interface PushResult {
  readonly batchId: number;
  readonly itemCount: number;
  readonly noopCount: number;
  readonly sent: number;
  readonly verified: number;
  readonly failed: number;
  readonly errors: readonly string[];
  readonly changes?: readonly {
    readonly productId: number;
    readonly wcProductId: number;
    readonly status: string;
    readonly error: string | null;
  }[];
}

export function WorkbenchCorrectionsPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [brand, setBrand] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [singleFieldOnly, setSingleFieldOnly] = useState<boolean>(false);
  const [conflictsOnly, setConflictsOnly] = useState<boolean>(false);
  const [failuresOnly, setFailuresOnly] = useState<boolean>(false);

  const [sendingIds, setSendingIds] = useState<readonly number[]>([]);
  const [readingIds, setReadingIds] = useState<readonly number[]>([]);
  const [adoptingIds, setAdoptingIds] = useState<readonly number[]>([]);
  const [sendingAll, setSendingAll] = useState<boolean>(false);
  const [fetchingAll, setFetchingAll] = useState<boolean>(false);
  const [sendProgress, setSendProgress] = useState<string | null>(null);

  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [pushBanner, setPushBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const [conflictConfirmTarget, setConflictConfirmTarget] = useState<CorrectionRow | null>(null);

  // Pending corrections query
  const correctionsQuery = useQuery({
    queryKey: ['corrections', brand],
    queryFn: () =>
      fetchJson<readonly CorrectionRow[]>(
        `/api/v1/review/corrections${brand !== 'all' ? `?brand=${encodeURIComponent(brand)}` : ''}`,
      ),
  });

  // Brands with counts
  const brandsQuery = useQuery({
    queryKey: ['correction-brands'],
    queryFn: () =>
      fetchJson<{
        readonly brands: readonly { readonly brand: string | null; readonly count: number }[];
        readonly truncated: boolean;
      }>('/api/v1/review/correction-brands'),
  });

  // Queue health check
  const queueHealthQuery = useQuery({
    queryKey: ['wc-queue-health'],
    queryFn: () => fetchJson<{ ok: boolean; error: string | null }>('/api/v1/wc-sync/queue-health'),
    refetchInterval: 60_000,
  });

  // Recent WAL batches
  const batchesQuery = useQuery({
    queryKey: ['wc-batches'],
    queryFn: () => fetchJson<readonly WcBatch[]>('/api/v1/wc-sync/batches'),
    refetchInterval: 8_000,
  });

  // Filtered displayed rows
  const displayedRows = useMemo(() => {
    let list = correctionsQuery.data ?? [];
    if (singleFieldOnly) {
      list = list.filter((r) => r.isSingleField === true);
    }
    if (conflictsOnly) {
      list = list.filter((r) => r.duplicateConflict !== null && r.duplicateConflict !== undefined);
    }
    if (failuresOnly) {
      list = list.filter((r) => Boolean(r.lastFailedError || rowErrors[r.id]));
    }
    const q = search.trim().toLowerCase();
    if (q !== '') {
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.sku && r.sku.toLowerCase().includes(q)) ||
          (r.newBarcode && r.newBarcode.includes(q)) ||
          (r.oldBarcode && r.oldBarcode.includes(q)) ||
          (r.brand && r.brand.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [correctionsQuery.data, singleFieldOnly, conflictsOnly, failuresOnly, search, rowErrors]);

  const stats = useMemo(() => {
    const list = correctionsQuery.data ?? [];
    const total = list.length;
    const singleField = list.filter((r) => r.isSingleField).length;
    const conflicts = list.filter((r) => r.duplicateConflict !== null && r.duplicateConflict !== undefined).length;
    const failures = list.filter((r) => Boolean(r.lastFailedError || rowErrors[r.id])).length;
    return { total, singleField, conflicts, failures };
  }, [correctionsQuery.data, rowErrors]);

  const pushErrorText = (error: unknown): string => {
    if (error instanceof ApiError) {
      const data = error.data as { error?: string; detail?: string } | null;
      const base = `${data?.error ?? 'erreur'}${data?.detail ? ` — ${data.detail}` : ''}`;
      return `HTTP ${error.status} : ${base}`;
    }
    return error instanceof Error ? error.message : String(error);
  };

  const pushScope = async (productIds: readonly number[]): Promise<PushResult> =>
    fetchJson<PushResult>('/api/v1/wc-sync/push', {
      method: 'POST',
      body: JSON.stringify({ productIds: [...productIds] }),
      signal: AbortSignal.timeout(150_000),
    });

  // Re-read live product state from WooCommerce
  const fetchLiveWoo = async (productId: number): Promise<void> => {
    setReadingIds((cur) => (cur.includes(productId) ? cur : [...cur, productId]));
    try {
      const res = await fetchJson<{ ok: boolean; barcodeRaw: string | null; name: string }>(
        `/api/v1/products/${productId}/fetch-live-woo`,
        { method: 'POST' },
      );
      setPushBanner({
        ok: true,
        text: `Données fraîches relues depuis WooCommerce pour "${res.name}" (Code Woo: ${res.barcodeRaw ?? 'aucun'}).`,
      });
      // Clear previous local error on this row
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
    } catch (err) {
      setPushBanner({
        ok: false,
        text: `Échec de la lecture directe WooCommerce : ${pushErrorText(err)}`,
      });
    } finally {
      setReadingIds((cur) => cur.filter((id) => id !== productId));
    }
  };

  // Batch re-read live product state for displayed items
  const fetchLiveBatch = async (): Promise<void> => {
    const ids = displayedRows.slice(0, 50).map((r) => r.id);
    if (ids.length === 0 || fetchingAll) return;
    setFetchingAll(true);
    try {
      const res = await fetchJson<{ ok: boolean; refreshedCount: number; errors: readonly string[] }>(
        '/api/v1/review/corrections/fetch-live',
        {
          method: 'POST',
          body: JSON.stringify({ productIds: ids }),
        },
      );
      if (res.errors.length > 0) {
        setPushBanner({
          ok: false,
          text: `${res.refreshedCount} produit(s) actualisé(s), ${res.errors.length} erreur(s) : ${res.errors[0]}`,
        });
      } else {
        setPushBanner({
          ok: true,
          text: `${res.refreshedCount} produit(s) actualisé(s) en direct depuis WooCommerce avec succès.`,
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
    } catch (err) {
      setPushBanner({
        ok: false,
        text: `Erreur d'actualisation globale Woo : ${pushErrorText(err)}`,
      });
    } finally {
      setFetchingAll(false);
    }
  };

  // Adopt site barcode as canonical
  const adoptSiteBarcode = async (productId: number): Promise<void> => {
    setAdoptingIds((cur) => (cur.includes(productId) ? cur : [...cur, productId]));
    try {
      const res = await fetchJson<{ ok: boolean; gtin: string }>(
        `/api/v1/products/${productId}/adopt-site-barcode`,
        { method: 'POST', body: JSON.stringify({ force: true }) },
      );
      setPushBanner({
        ok: true,
        text: `Code du site (${res.gtin}) validé et adopté comme GTIN canonique. Produit aligné !`,
      });
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
    } catch (err) {
      setPushBanner({
        ok: false,
        text: `Échec de l'adoption du code du site : ${pushErrorText(err)}`,
      });
    } finally {
      setAdoptingIds((cur) => cur.filter((id) => id !== productId));
    }
  };

  // Push single correction
  const sendOne = async (row: CorrectionRow, force = false): Promise<void> => {
    if (!force && row.duplicateConflict) {
      setConflictConfirmTarget(row);
      return;
    }
    const correctionId = row.id;
    setSendingIds((cur) => (cur.includes(correctionId) ? cur : [...cur, correctionId]));
    try {
      const r = await pushScope([correctionId]);
      if (r.failed > 0) {
        const rowErr = r.changes?.find((c) => c.productId === correctionId)?.error ?? r.errors[0] ?? 'Échec inconnu';
        setRowErrors((prev) => ({ ...prev, [correctionId]: rowErr }));
        const text = `Échec de l'envoi (Lot #${r.batchId}) : ${rowErr}`;
        setPushBanner({ ok: false, text });
      } else if (r.sent === 0 && r.noopCount > 0) {
        setPushBanner({ ok: true, text: 'Déjà aligné : WooCommerce possède déjà exactement ce code-barres.' });
        setRowErrors((prev) => {
          const next = { ...prev };
          delete next[correctionId];
          return next;
        });
      } else {
        setPushBanner({
          ok: true,
          text: `Envoyé et vérifié sur WooCommerce (Lot #${r.batchId}) : les 2 champs (Core + Plugin) sont confirmés !`,
        });
        setRowErrors((prev) => {
          const next = { ...prev };
          delete next[correctionId];
          return next;
        });
      }
    } catch (err) {
      const msg = pushErrorText(err);
      setRowErrors((prev) => ({ ...prev, [correctionId]: msg }));
      setPushBanner({ ok: false, text: `Échec de l'envoi : ${msg}` });
    } finally {
      setSendingIds((cur) => cur.filter((id) => id !== correctionId));
      void queryClient.invalidateQueries({ queryKey: ['wc-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      void queryClient.invalidateQueries({ queryKey: ['correction-brands'] });
    }
  };

  // Push all displayed
  const sendFiltered = async (): Promise<void> => {
    const ids = displayedRows.map((r) => r.id);
    if (ids.length === 0 || sendingAll) return;
    setSendingAll(true);
    setSendProgress(`Envoi 0 / ${ids.length}…`);
    const CHUNK = 5;
    let verified = 0;
    let failed = 0;
    let lastBatchId: number | null = null;
    const newErrors: Record<number, string> = {};

    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        setSendProgress(`Envoi ${Math.min(i + CHUNK, ids.length)} / ${ids.length}…`);
        const slice = ids.slice(i, i + CHUNK);
        const r = await pushScope(slice);
        verified += r.verified;
        failed += r.failed;
        if (r.batchId > 0) lastBatchId = r.batchId;

        if (r.changes) {
          for (const ch of r.changes) {
            if (ch.status === 'failed' && ch.error) {
              newErrors[ch.productId] = ch.error;
            }
          }
        }
      }
      setRowErrors((prev) => ({ ...prev, ...newErrors }));
      const text =
        failed > 0
          ? `Envoi terminé : ${verified} vérifié(s), ${failed} échec(s) — détail affiché sous chaque produit en échec.`
          : `Envoi terminé avec succès : ${verified} produit(s) vérifié(s) et relu(s) OK${lastBatchId ? ` (Lot #${lastBatchId})` : ''}.`;
      setPushBanner({ ok: failed === 0, text });
    } catch (err) {
      setPushBanner({
        ok: false,
        text: `Envoi interrompu à ${verified + failed} / ${ids.length} : ${pushErrorText(err)}`,
      });
    } finally {
      setSendingAll(false);
      setSendProgress(null);
      void queryClient.invalidateQueries({ queryKey: ['wc-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      void queryClient.invalidateQueries({ queryKey: ['correction-brands'] });
    }
  };

  // Hide bulk mutation
  const hideBulkMutation = useMutation({
    mutationFn: (productIds: readonly number[]) =>
      fetchJson<{ batchId: number; sent: number; verified: number; failed: number }>(
        '/api/v1/products/hide-bulk',
        {
          method: 'POST',
          body: JSON.stringify({ productIds: [...productIds] }),
        },
      ),
    onSuccess: (data) => {
      setPushBanner({
        ok: true,
        text: `Produit(s) masqué(s) : lot #${data.batchId} (${data.verified} vérifié(s)).`,
      });
      void queryClient.invalidateQueries({ queryKey: ['corrections'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      void queryClient.invalidateQueries({ queryKey: ['wc-batches'] });
    },
    onError: (err) => {
      setPushBanner({ ok: false, text: `Échec du masquage : ${String(err.message)}` });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Corrections WooCommerce"
        subtitle="Produits dont le GTIN approuvé diffère du code publié sur le site. Poussée sécurisée avec écriture dans les 2 champs (GTIN standard + REF plugin) et relecture de vérification immédiate."
        meta={[
          { label: 'À corriger', value: `${stats.total.toLocaleString('fr-FR')} produits` },
          { label: '1 seul champ sur Woo', value: String(stats.singleField) },
          { label: 'Doublons détectés', value: String(stats.conflicts) },
          { label: 'Échecs récents', value: String(stats.failures) },
        ]}
      />

      {/* Push Engine Health & Audit Panel */}
      <Panel>
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-ink">Moteur de synchronisation WooCommerce (P3)</p>
              <p className="mt-0.5 text-xs text-ink-soft">
                Chaque envoi crée un lot WAL traçable, pousse les deux champs (core + plugin), puis relit la fiche produit en direct pour prouver l&apos;alignement.
              </p>
            </div>
            {queueHealthQuery.data !== undefined && (
              <Badge tone={queueHealthQuery.data.ok ? 'good' : 'alarm'}>
                {queueHealthQuery.data.ok ? 'File Redis active' : 'File Redis indisponible'}
              </Badge>
            )}
          </div>

          {queueHealthQuery.data !== undefined && !queueHealthQuery.data.ok && (
            <Alert tone="alarm" title="File d'attente Redis injoignable">
              {queueHealthQuery.data.error ?? 'Détail inconnu'}. Vérifiez la variable REDIS_URL de l&apos;API dans Coolify.
            </Alert>
          )}

          {batchesQuery.data !== undefined && batchesQuery.data.length > 0 && (
            <div className="space-y-1.5 border-t border-border pt-2.5">
              <p className="text-[11px] font-semibold text-ink-faint uppercase tracking-wider">Derniers lots WAL d&apos;envoi :</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {batchesQuery.data.slice(0, 6).map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between rounded-lg border border-border bg-card/60 px-2.5 py-1.5 font-mono text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-ink">#{b.id}</span>
                      <Badge
                        tone={
                          b.status === 'completed'
                            ? 'good'
                            : b.status === 'running'
                              ? 'signal'
                              : b.status === 'failed'
                                ? 'alarm'
                                : 'muted'
                        }
                      >
                        {b.status}
                      </Badge>
                      <span className="text-ink-soft">{b.changeCount} chg</span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      {b.succeeded > 0 && <span className="text-herb-700 font-bold">{b.succeeded} ok</span>}
                      {b.failed > 0 && <span className="text-rust-600 font-bold">{b.failed} err</span>}
                      <span className="text-ink-faint">{stamp(b.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </PanelBody>
      </Panel>

      {/* Global push / action banner */}
      {pushBanner !== null && (
        <Alert
          tone={pushBanner.ok ? 'good' : 'alarm'}
          title={pushBanner.ok ? 'Succès de l’opération' : 'Attention / Erreur'}
          actions={
            <Button type="button" variant="ghost" size="xs" onClick={() => setPushBanner(null)}>
              Fermer
            </Button>
          }
        >
          {pushBanner.text}
        </Alert>
      )}

      {/* Controls & Filter Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 shadow-xs">
        <div className="flex flex-wrap items-center gap-2">
          {/* Brand select */}
          <div className="flex items-center gap-1.5">
            <label htmlFor="correction-brand-select" className="text-xs font-semibold text-ink-soft">
              Marque :
            </label>
            <select
              id="correction-brand-select"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="rounded-lg border border-border bg-paper px-2.5 py-1 text-xs font-semibold text-ink shadow-2xs outline-none focus:border-herb-600"
            >
              <option value="all">
                Toutes marques ({brandsQuery.data?.brands.reduce((n, b) => n + b.count, 0) ?? 0})
              </option>
              {brandsQuery.data?.brands.map((b) => (
                <option key={b.brand ?? '__none__'} value={b.brand ?? '__none__'}>
                  {(b.brand ?? '— Sans marque —') + ` (${b.count})`}
                </option>
              ))}
            </select>
          </div>

          {/* Search bar */}
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Chercher (nom, SKU, code)..."
              className="h-7 w-48 rounded-lg border border-border bg-paper pl-7 pr-2 font-mono text-xs text-ink placeholder:text-ink-ghost outline-none focus:border-herb-600"
            />
            <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-ink-ghost pointer-events-none" />
          </div>

          {/* Quick toggle filters */}
          <button
            type="button"
            onClick={() => setSingleFieldOnly(!singleFieldOnly)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
              singleFieldOnly
                ? 'bg-amber-600 text-white shadow-xs'
                : 'border border-amber-500/40 bg-amber-50/60 text-amber-800 hover:bg-amber-100/80 dark:bg-amber-950/30 dark:text-amber-300'
            }`}
            title="Produits avec 1 seul champ renseigné sur WooCommerce"
          >
            <span>⚠️ 1 seul champ Woo</span>
            <span className="rounded-full bg-black/15 px-1.5 py-0.2 text-[10px]">
              {stats.singleField}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setConflictsOnly(!conflictsOnly)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
              conflictsOnly
                ? 'bg-rust-600 text-white shadow-xs'
                : 'border border-rust-500/40 bg-rust-50/60 text-rust-800 hover:bg-rust-100/80 dark:bg-rust-950/30 dark:text-rust-300'
            }`}
            title="Produits dont le code est déjà attribué à un autre produit (conflit de doublon)"
          >
            <span>⚠️ Doublons</span>
            <span className="rounded-full bg-black/15 px-1.5 py-0.2 text-[10px]">
              {stats.conflicts}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setFailuresOnly(!failuresOnly)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
              failuresOnly
                ? 'bg-red-700 text-white shadow-xs'
                : 'border border-red-500/40 bg-red-50/60 text-red-800 hover:bg-red-100/80 dark:bg-red-950/30 dark:text-red-300'
            }`}
            title="Produits ayant échoué lors du dernier envoi vers WooCommerce"
          >
            <span>❌ Échecs</span>
            <span className="rounded-full bg-black/15 px-1.5 py-0.2 text-[10px]">
              {stats.failures}
            </span>
          </button>
        </div>

        {/* Global batch actions */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fetchingAll || displayedRows.length === 0}
            onClick={() => void fetchLiveBatch()}
            title="Re-lit directement les données fraîches depuis l'API WooCommerce pour les produits affichés"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${fetchingAll ? 'animate-spin' : ''}`} />
            {fetchingAll ? 'Re-lecture Woo…' : `Tout actualiser depuis Woo (${Math.min(displayedRows.length, 50)})`}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={displayedRows.length === 0 || hideBulkMutation.isPending || sendingAll}
            onClick={() => {
              const ids = displayedRows.map((r) => r.id);
              if (ids.length > 0) hideBulkMutation.mutate(ids);
            }}
            title="Masque tous les produits affichés dans WooCommerce via un lot vérifié"
          >
            <EyeOff className="h-3.5 w-3.5 mr-1.5" />
            {hideBulkMutation.isPending ? 'Masquage…' : `Tout masquer (${displayedRows.length})`}
          </Button>

          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={displayedRows.length === 0 || sendingAll}
            onClick={() => void sendFiltered()}
            title="Envoie et vérifie les corrections du filtre courant vers WooCommerce"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {sendingAll ? (sendProgress ?? 'Envoi en cours…') : `Tout envoyer (${displayedRows.length})`}
          </Button>
        </div>
      </div>

      {/* Main product list */}
      {correctionsQuery.isPending && <Loading label="Chargement des corrections en attente…" />}

      {correctionsQuery.isError && (
        <Alert tone="alarm" title="Erreur de chargement des corrections">
          {String(correctionsQuery.error.message)}
        </Alert>
      )}

      {correctionsQuery.data !== undefined && displayedRows.length === 0 && (
        <EmptyState
          title="Aucune correction correspondante"
          hint={
            stats.total === 0
              ? 'Le catalogue WooCommerce est entièrement aligné sur les codes GTIN approuvés !'
              : 'Aucun produit ne correspond aux filtres actuels. Modifiez les critères de recherche.'
          }
        />
      )}

      <div className="space-y-3">
        {displayedRows.map((row) => {
          const isSending = sendingIds.includes(row.id);
          const isReading = readingIds.includes(row.id);
          const isAdopting = adoptingIds.includes(row.id);
          const currentError = rowErrors[row.id] ?? row.lastFailedError;

          return (
            <div
              key={row.id}
              className={`rounded-xl border p-4 shadow-xs transition ${
                row.duplicateConflict
                  ? 'border-rust-500/50 bg-rust-50/20 dark:bg-rust-950/10'
                  : currentError
                    ? 'border-red-500/50 bg-red-50/20 dark:bg-red-950/10'
                    : 'border-border bg-card'
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Product details */}
                <div className="flex items-start gap-3.5 min-w-0 flex-1">
                  {row.image ? (
                    <img
                      src={row.image}
                      alt=""
                      className="h-14 w-14 rounded-lg border border-border object-cover bg-cream shrink-0"
                    />
                  ) : (
                    <div className="grid h-14 w-14 place-items-center rounded-lg border border-border bg-card-faint text-ink-ghost shrink-0">
                      <BarcodeIcon className="h-6 w-6" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-bold text-ink leading-snug">
                        {toCapitalizedCase(row.name)}
                      </p>
                      {row.isVariation && (
                        <span className="rounded bg-paper px-1.5 py-0.2 text-[10px] font-mono text-ink-soft border border-border">
                          Variante
                        </span>
                      )}
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-ink-soft">
                      <span>SKU: <strong className="text-ink">{row.sku ?? '∅'}</strong></span>
                      <span>·</span>
                      <span>{row.brand ?? '— Sans marque —'}</span>
                      {row.purchasePriceHt !== null && (
                        <>
                          <span>·</span>
                          <span>Achat {money(row.purchasePriceHt)} HT</span>
                        </>
                      )}
                      <span>·</span>
                      <span className="text-ink-faint">
                        {row.lastSeenAt ? `Woo vu le ${stamp(row.lastSeenAt)}` : 'Non actualisé récemment'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Barcode Comparison Display */}
                <div className="flex flex-col lg:items-end gap-1.5 shrink-0 font-mono text-xs">
                  <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-paper px-3 py-1.5 shadow-2xs">
                    {/* Site barcode */}
                    <div className="flex flex-col items-start lg:items-end">
                      <span className="text-[10px] uppercase tracking-wider text-ink-faint">Sur Woo</span>
                      <span className="text-rust-600 line-through font-bold">
                        {row.coreGtin || row.pluginEan ? (
                          <>
                            <span title="GTIN standard (core)">{row.coreGtin ?? '∅'}</span>
                            <span className="text-ink-faint mx-1">/</span>
                            <span title="REF alg_ean (plugin)">{row.pluginEan ?? '∅'}</span>
                          </>
                        ) : (
                          row.oldBarcode ?? '∅ (aucun)'
                        )}
                      </span>
                    </div>

                    <ArrowRight className="h-4 w-4 text-ink-ghost shrink-0" />

                    {/* Target canonical code */}
                    <div className="flex flex-col items-start lg:items-end">
                      <span className="text-[10px] uppercase tracking-wider text-ink-faint">Nouveau canonique</span>
                      <Gtin gtin={row.newBarcode} tone="good" showBarcode={false} />
                    </div>
                  </div>

                  {row.newBarcode && (
                    <div className="self-start lg:self-end">
                      <Barcode value={row.newBarcode} tone="good" height={16} />
                    </div>
                  )}

                  {row.isSingleField && (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                      ⚠️ 1 seul champ rempli sur Woo
                    </span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {/* Fetch Live from WooCommerce */}
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    disabled={isReading || isSending || isAdopting}
                    onClick={() => void fetchLiveWoo(row.id)}
                    title="Re-lit immédiatement la fiche en direct depuis WooCommerce pour rafraîchir le code et les prix"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${isReading ? 'animate-spin' : ''}`} />
                    {isReading ? 'Re-lecture…' : 'Re-lire Woo'}
                  </Button>

                  {/* Adopt Site Barcode if valid */}
                  {row.canAdoptSiteBarcode && (
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      disabled={isReading || isSending || isAdopting}
                      onClick={() => void adoptSiteBarcode(row.id)}
                      className="border-herb-600/40 text-herb-700 hover:bg-herb-50 dark:hover:bg-herb-950/30"
                      title="Le code du site est valide ! Cliquez pour l'adopter comme GTIN canonique et aligner immédiatement ce produit."
                    >
                      <Check className="h-3 w-3 mr-1" />
                      {isAdopting ? 'Adoption…' : `Adopter code site (${row.siteBarcode})`}
                    </Button>
                  )}

                  {/* Hide button */}
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    disabled={isSending || sendingAll || hideBulkMutation.isPending}
                    onClick={() => hideBulkMutation.mutate([row.id])}
                    title="Masque ce produit sur WooCommerce (catalog_visibility=hidden)"
                  >
                    Masquer
                  </Button>

                  {/* Send to WooCommerce */}
                  <Button
                    type="button"
                    variant="primary"
                    size="xs"
                    disabled={isSending || sendingAll}
                    onClick={() => void sendOne(row)}
                    title="Pousse le nouveau GTIN sur WooCommerce (écrit Core + Plugin, puis vérifie par relecture)"
                  >
                    <Send className="h-3 w-3 mr-1" />
                    {isSending ? 'Envoi…' : 'Envoyer'}
                  </Button>
                </div>
              </div>

              {/* Duplicate Conflict Warning Box */}
              {row.duplicateConflict && (
                <div className="mt-3 rounded-lg border border-rust-500/30 bg-rust-500/10 p-2.5 text-xs text-rust-900 dark:text-rust-200">
                  <div className="flex items-center gap-2 font-bold">
                    <AlertTriangle className="h-4 w-4 text-rust-600 shrink-0" />
                    <span>Conflit de code en doublon détecté :</span>
                  </div>
                  <p className="mt-0.5 ml-6">
                    Le code-barres <strong>{row.duplicateConflict.matchedBarcode}</strong> est déjà utilisé dans le catalogue par le produit{' '}
                    <strong>[{row.duplicateConflict.sku ?? 'sans SKU'}] {row.duplicateConflict.name}</strong> ({row.duplicateConflict.brand ?? 'sans marque'}, champ: {row.duplicateConflict.field}).
                    Envoyer ce code écrasera ou créera une collision dans WooCommerce.
                  </p>
                </div>
              )}

              {/* Last Push Failure Error Box */}
              {currentError && (
                <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-900 dark:text-red-200">
                  <div className="flex items-center gap-2 font-bold">
                    <XCircle className="h-4 w-4 text-red-600 shrink-0" />
                    <span>Échec du dernier envoi vers WooCommerce :</span>
                    {row.lastFailedBatchId && (
                      <span className="font-mono text-[11px] text-ink-faint">(Lot #{row.lastFailedBatchId})</span>
                    )}
                  </div>
                  <p className="mt-0.5 ml-6 font-mono text-[11px] break-words text-red-700 dark:text-red-300">
                    {currentError}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Conflict Send Confirmation Dialog */}
      <ConfirmDialog
        open={conflictConfirmTarget !== null}
        title="Attention : Envoi avec conflit de doublon"
        confirmLabel="Forcer l'envoi"
        tone="alarm"
        onConfirm={() => {
          if (conflictConfirmTarget) {
            const target = conflictConfirmTarget;
            setConflictConfirmTarget(null);
            void sendOne(target, true);
          }
        }}
        onCancel={() => setConflictConfirmTarget(null)}
      >
        <p className="text-sm text-ink leading-relaxed">
          Le code-barres <strong>{conflictConfirmTarget?.newBarcode}</strong> est déjà attribué à{' '}
          <strong>[{conflictConfirmTarget?.duplicateConflict?.sku}] {conflictConfirmTarget?.duplicateConflict?.name}</strong>.
          Voulez-vous quand même forcer l&apos;envoi de ce code pour &quot;{conflictConfirmTarget?.name}&quot; ?
        </p>
      </ConfirmDialog>
    </div>
  );
}
